import type { ActiveTool } from './toolbar/use-canvas-tool';
import type { ShapeKind } from '@/features/canvas/engine/nodes/shape/shapes';

export interface GhostSize { width: number; height: number }

/** Default placement size by tool kind. */
export const GHOST_SIZES: Record<string, GhostSize> = {
  sticky:   { width: 160, height: 160 },
  shape:    { width: 120, height: 120 },
  text:     { width: 120, height: 32  },
  frame:    { width: 240, height: 160 },
  freedraw: { width: 0,   height: 0   },
  emoji:    { width: 80,  height: 80  },
  gif:      { width: 200, height: 200 },
};

/**
 * Per-shapeKind overrides. Most shapes use the square `shape` default, but a
 * few read wrong at 1:1 — e.g. a pill (stadium) at a square aspect ratio is
 * just a circle, so it spawns wide so it immediately reads as a pill. The
 * shape stays a true stadium at any aspect ratio when the user resizes it.
 */
const SHAPE_KIND_SIZES: Partial<Record<ShapeKind, GhostSize>> = {
  pill: { width: 160, height: 48 },
};

/**
 * Resolves the default placement size for a tool — the size used for BOTH the
 * placement ghost preview and the persisted node on drop (single source of
 * truth). Returns null for tools without a fixed size (e.g. freedraw, or any
 * tool whose default size is 0).
 */
export function resolvePlacementSize(activeTool: ActiveTool): GhostSize | null {
  if (activeTool.kind === 'frame' && activeTool.size) return activeTool.size;
  const base =
    activeTool.kind === 'shape'
      ? SHAPE_KIND_SIZES[activeTool.shapeKind] ?? GHOST_SIZES.shape
      : GHOST_SIZES[activeTool.kind];
  if (!base || base.width <= 0) return null;
  return base;
}
